// Pure helpers of the Week tab: what a cell shows and the totals. They read
// statuses in the one vocabulary payroll uses (present / absent / sick /
// leave; 'late' and 'approved' without a leave type are present, 'vacation'
// and 'approved' with a type are leave).

export const hhmm = (time) => (time ? String(time).slice(0, 5) : '');
const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};
export const fmtHours = (h) => `${num(h).toFixed(1)} h`;

export const kindOf = (record) => {
  if (!record) return 'empty';
  const { status } = record;
  if (status === 'late' || (status === 'approved' && record.leave_type_id == null) || status === 'present') {
    if (record.check_in_time && !record.check_out_time) return 'open';
    if (record.check_in_time && record.check_out_time && String(record.check_out_time) < String(record.check_in_time)) return 'night';
    return 'present';
  }
  if (status === 'vacation') return 'leave'; // older rows: paid leave
  if (status === 'leave' || status === 'approved') return record.leave_is_paid ? 'leave' : 'unpaid';
  if (status === 'absent' || status === 'sick') return status;
  return 'present';
};

export const isWorked = (kind) => kind === 'present' || kind === 'night' || kind === 'open';

const overtimeLabel = (record, t) => {
  const minutes = parseInt(record.overtime_minutes, 10) || 0;
  const hours = num(record.overtime_hours);
  if (minutes > 0) return t('Overtime {minutes} min', { minutes });
  if (hours > 0) return t('Overtime {hours} h', { hours: hours.toFixed(1) });
  return '';
};

/** What one day's cell shows: kind (colour), two lines and flags. */
export const cellView = (record, t) => {
  const kind = kindOf(record);
  if (kind === 'empty') return { kind, line1: '+', line2: '', flags: [] };
  const flags = [];
  if (record.is_late && parseInt(record.late_minutes, 10) > 0) flags.push(t('Late {minutes} min', { minutes: parseInt(record.late_minutes, 10) }));
  const ot = overtimeLabel(record, t);
  if (ot) flags.push(ot);
  switch (kind) {
    case 'present':
      return {
        kind,
        line1: record.check_in_time ? `${hhmm(record.check_in_time)} – ${hhmm(record.check_out_time)}` : t('Present'),
        line2: fmtHours(record.hours_worked),
        flags,
      };
    case 'night':
      return { kind, line1: `${hhmm(record.check_in_time)} → ${hhmm(record.check_out_time)}`, line2: `${fmtHours(record.hours_worked)} · ${t('night')}`, flags };
    case 'open':
      return { kind, line1: `${hhmm(record.check_in_time)} → …`, line2: t('open'), flags };
    case 'absent':
      return { kind, line1: t('Absent'), line2: '0 h', flags };
    case 'sick':
      return { kind, line1: t('Sick'), line2: t('sick pay'), flags };
    case 'leave':
      return { kind, line1: record.leave_type_name || t('Leave'), line2: t('Paid leave'), flags };
    case 'unpaid':
      return { kind, line1: record.leave_type_name || t('Leave'), line2: t('Unpaid leave'), flags };
    default:
      return { kind, line1: '', line2: '', flags };
  }
};

/**
 * One employee's week: hours worked (present days), of which on Sunday and
 * on public holidays, paid leave hours, overtime, absences and sick days.
 * `days` comes from the server (calendar Sunday / holiday per date).
 */
export const employeeTotals = (records, days) => {
  const dayByDate = new Map(days.map((d) => [d.date, d]));
  const totals = { worked: 0, sunday: 0, holiday: 0, paidLeave: 0, overtimeMinutes: 0, absent: 0, sick: 0, unpaid: 0 };
  records.forEach((r) => {
    const kind = kindOf(r);
    const day = dayByDate.get(r.date) || {};
    if (isWorked(kind)) {
      const h = num(r.hours_worked);
      totals.worked += h;
      if (day.is_holiday || r.is_holiday) totals.holiday += h;
      else if (day.is_sunday || r.is_sunday) totals.sunday += h;
    }
    if (kind === 'leave') totals.paidLeave += num(r.hours_worked);
    if (kind === 'absent') totals.absent += 1;
    if (kind === 'sick') totals.sick += 1;
    if (kind === 'unpaid') totals.unpaid += 1;
    totals.overtimeMinutes += (parseInt(r.overtime_minutes, 10) || 0) + Math.round(num(r.overtime_hours) * 60);
  });
  return totals;
};

/** The small line under an employee's weekly total. */
export const totalsNote = (totals, t) => {
  const parts = [];
  if (totals.holiday > 0) parts.push(t('holiday {hours}', { hours: fmtHours(totals.holiday) }));
  if (totals.sunday > 0) parts.push(t('Sunday {hours}', { hours: fmtHours(totals.sunday) }));
  if (totals.paidLeave > 0) parts.push(t('paid leave {hours}', { hours: fmtHours(totals.paidLeave) }));
  if (totals.overtimeMinutes > 0) parts.push(t('overtime {minutes} min', { minutes: totals.overtimeMinutes }));
  if (totals.absent > 0) parts.push(t('{count} absent', { count: totals.absent }));
  if (totals.sick > 0) parts.push(t('{count} sick', { count: totals.sick }));
  if (totals.unpaid > 0) parts.push(t('{count} unpaid', { count: totals.unpaid }));
  return parts.join(' · ');
};

/** "SRD 300 / h" for hourly employees, "SRD 50,000 / month" for monthly ones. */
export const payLabel = (employee, t, locale) => {
  const currency = employee.currency || 'SRD';
  const fmt = (v) => num(v).toLocaleString(locale || 'en-US', { maximumFractionDigits: 2 });
  if (employee.payment_type === 'hourly') {
    return employee.hourly_rate ? t('{currency} {amount} / h', { currency, amount: fmt(employee.hourly_rate) }) : t('No hourly rate');
  }
  return employee.salary ? t('{currency} {amount} / month', { currency, amount: fmt(employee.salary) }) : t('No salary');
};

// Date-only helpers (UTC arithmetic, no time zone shifts).
export const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
export const isoWeek = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3); // Thursday of this week
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
};
export const cellKey = (employeeId, date) => `${Number(employeeId)}|${date}`;
